import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Button,
  Chip,
  CircularProgress,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import MessageIcon from '@mui/icons-material/Message';
import TaskIcon from '@mui/icons-material/Task';

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [stats, setStats] = useState({});
  const [overallStats, setOverallStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProjects();
    fetchStats();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await axios.get('/api/projects');
      setProjects(response.data.projects || []);
    } catch (err) {
      console.error('Error fetching projects:', err);
    }
  };

  const fetchStats = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/projects/stats');
      setStats(response.data.stats || {});
      setOverallStats(response.data.overallStats || null);
    } catch (err) {
      console.error('Error fetching stats:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <Container maxWidth="lg">
        <Box sx={{ mt: 4, mb: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            Панель управления
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Добро пожаловать, {user?.username}!
          </Typography>
        </Box>

        {/* Общая статистика для супер админа */}
        {user?.role === 'super_admin' && overallStats && (
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" component="h2" gutterBottom sx={{ mb: 2 }}>
              Общая статистика
            </Typography>
            <Grid container spacing={2} sx={{ mb: 4 }}>
              <Grid item xs={12} sm={4}>
                <Card>
                  <CardContent>
                    <Typography variant="h4" color="primary" gutterBottom>
                      {overallStats.totalProjects}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Всего проектов
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Card>
                  <CardContent>
                    <Typography variant="h4" color="primary" gutterBottom>
                      {overallStats.totalChats}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Всего чатов
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Card>
                  <CardContent>
                    <Typography variant="h4" color="primary" gutterBottom>
                      {overallStats.totalTasks}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Всего задач
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Статистика по проектам */}
        {projects.length > 0 && (
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" component="h2" gutterBottom sx={{ mb: 2 }}>
              Статистика по проектам
            </Typography>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Grid container spacing={2}>
                {projects.map((project) => {
                  const projectStats = stats[project.uuid] || { unreadMessages: 0, activeTasks: 0 };
                  return (
                    <Grid item xs={12} sm={6} md={4} key={project.uuid}>
                      <Card>
                        <CardContent>
                          <Typography variant="h6" gutterBottom>
                            {project.name}
                          </Typography>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <MessageIcon fontSize="small" color="primary" />
                              <Typography variant="body2" sx={{ flex: 1 }}>
                                Непрочитанных сообщений:
                              </Typography>
                              <Chip
                                label={projectStats.unreadMessages}
                                size="small"
                                color={projectStats.unreadMessages > 0 ? 'error' : 'default'}
                              />
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <TaskIcon fontSize="small" color="primary" />
                              <Typography variant="body2" sx={{ flex: 1 }}>
                                Активных задач:
                              </Typography>
                              <Chip
                                label={projectStats.activeTasks}
                                size="small"
                                color={projectStats.activeTasks > 0 ? 'warning' : 'default'}
                              />
                            </Box>
                          </Box>
                          <Button
                            variant="outlined"
                            fullWidth
                            sx={{ mt: 2 }}
                            onClick={() => navigate(`/projects/${project.uuid}`)}
                          >
                            Открыть проект
                          </Button>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </Box>
        )}

        <Grid container spacing={3}>
          {(user?.role === 'admin' || user?.role === 'super_admin') && (
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Управление пользователями
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Добавляйте и управляйте сотрудниками системы
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={() => navigate('/users')}
                  >
                    Перейти
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          )}

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Проекты
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Создавайте и управляйте проектами для организации чатов
                </Typography>
                <Button variant="contained" onClick={() => navigate('/projects')}>
                  Перейти
                </Button>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Управление чатами
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Создавайте и управляйте подключенными Telegram чатами
                </Typography>
                <Button variant="contained" onClick={() => navigate('/chats')}>
                  Перейти
                </Button>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Сообщения
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Переписка с подключенными Telegram чатами
                </Typography>
                <Button variant="contained" onClick={() => navigate('/messages')}>
                  Перейти
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Layout>
  );
};

export default Dashboard;
